
import { Component, Vue, Watch } from 'vue-property-decorator';

import { sharedState } from '../../../../../framework/state';
import { getResource, postResource } from '../../../../../framework/client/resource';
import TextField from '../../../../../framework/fields/TextField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import { VendorContractType } from '../../../../../../common/application/model/enums/VendorContractType';
import { VendorContract } from '../../../../../../common/application/model/VendorContract';
import { getEnumerationOptions } from '../../../../../framework/service/options';
import SwitchField from '../../../../../framework/fields/SwitchField.vue';
import AutoCompleteField from '../../../../../framework/fields/AutoCompleteField.vue';
import {
    getAccountOptions,
    getCustomerOptions,
    getVendorOptions,
} from '../../../../service/application_options_service';
import NumberField from '../../../../../framework/fields/NumberField.vue';
import { VendorContractScope } from '../../../../../../common/application/model/enums/VendorContractScope';
import DateField from '../../../../../framework/fields/DateField.vue';
import { PaymentType } from '../../../../../../common/application/model/enums/PaymentType';
import { BudgetType } from '../../../../../../common/application/model/enums/BudgetType';
import { AccountType } from '../../../../../../common/application/model/enums/AccountType';
import { Vendor } from '../../../../../../common/application/model/Vendor';
import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';
import { ValidationObserver } from 'vee-validate';
import CurrencyAmountField from '../../../../../framework/fields/CurrencyAmountField.vue';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { errorToast, successToast } from '../../../../service/toast_service';

@Component({
    components: {
        DateField,
        NumberField,
        SwitchField,
        SelectField,
        TextField,
        CurrencyAmountField,
        ValidationObserver,
        AutoCompleteField,
    },
})
export default class AddVendorContract extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VENDOR_CONTRACT;

    // Properties

    // State
    shared = sharedState;
    local = {
        row: {} as VendorContract,
        customerOptions: [] as { id: string | undefined; label: string }[],
        vendorOptions: [] as { id: string | undefined; label: string }[],
        vehicleContractOptions: [] as { id: string | undefined; label: string }[],
        vehicleContractQuery: undefined as Map<string, string> | undefined,
        vendorContractTypeOptions: [] as { id: string | undefined; label: string }[],
        vendorContractScopeOptions: [] as { id: string | undefined; label: string }[],
        paymentTypeOptions: [] as { id: string | undefined; label: string }[],
        budgetTypeOptions: [] as { id: string | undefined; label: string }[],
        customerExpenseAccountOptions: [] as { id: string | undefined; label: string }[],
        vendorLocationParams: new Map() as Map<string, string>,
        loading: true,
    };

    // Functions
    async mounted() {
        this.local.loading = true;
        this.local.customerOptions = await getCustomerOptions();
        this.local.vendorOptions = await getVendorOptions();
        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );
        this.local.vendorContractScopeOptions = getEnumerationOptions(
            'VendorContractScope',
            Object.keys(VendorContractScope),
            this,
        );

        this.local.paymentTypeOptions = getEnumerationOptions('PaymentType', Object.keys(PaymentType), this);
        this.local.budgetTypeOptions = getEnumerationOptions('BudgetType', Object.keys(BudgetType), this);

        if (this.$route.query.vehicleContractId) {
            const vehicleContract = await getResource<VehicleContract>(
                ApplicationResource.VEHICLE_CONTRACT,
                this.$route.query.vehicleContractId as string,
            );

            if (vehicleContract) {
                this.local.row = {
                    ...this.local.row,
                    vehicleContractId: vehicleContract.id,
                    vehicleRegistrationNumber: vehicleContract.vehicleRegistrationNumber ?? '',
                    customerId: vehicleContract.customerId,
                    startDate: vehicleContract.smartFleetContractStart ?? new Date(),
                    endDate: vehicleContract.smartFleetContractEnd,
                };
            }
        }

        this.local.loading = false;
    }

    @Watch('local.row.customerId')
    async customerIdChange() {
        this.updateVehicleContractQuery();
        this.local.customerExpenseAccountOptions = await getAccountOptions(
            this.local.row.customerId,
            AccountType.EXPENSE,
        );
    }

    @Watch('local.row.vendorId')
    async vendorIdChange(vendorId: string | undefined) {
        if (vendorId) {
            const vendor = await getResource<Vendor>(ApplicationResource.VENDOR, vendorId);
            if (vendor) {
                this.local.row = {
                    ...this.local.row,
                    vendorNumber: vendor.vendorNumber,
                };
            }
        } else {
            this.local.row = {
                ...this.local.row,
                vendorNumber: '',
            };
        }
    }

    @Watch('local.row.vehicleContractId')
    async vehicleContractIdChange(vehicleContractId: string | undefined) {
        if (vehicleContractId) {
            const vehicleContract = await getResource<VehicleContract>(
                ApplicationResource.VEHICLE_CONTRACT,
                vehicleContractId,
            );
            if (vehicleContract) {
                this.local.row = {
                    ...this.local.row,
                    vehicleRegistrationNumber: vehicleContract.vehicleRegistrationNumber ?? '',
                };
            }
        } else {
            this.local.row = {
                ...this.local.row,
                vehicleRegistrationNumber: '',
            };
        }
    }

    async add() {
        await this.addResource();
    }

    async addAndClose() {
        await this.addResource(true);
    }

    private async addResource(goBack?: boolean) {
        try {
            await postResource<VendorContract>(this.resourceType, this.local.row);
            successToast(this, 'message.vendorContractSaved');

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }

    updateVehicleContractQuery() {
        if (!this.local.row.customerId) {
            this.local.vehicleContractQuery = undefined;
        } else {
            this.local.vehicleContractQuery = new Map([['customerId', this.local.row.customerId]]);
        }
    }

    get vendorLocationSelectorParams(): Map<string, string> {
        const params = new Map();
        if (this.local.row.vendorId) {
            params.set('parentId', this.local.row.vendorId);
        }

        return params;
    }
}
